/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { isNotNil } from 'es-toolkit';

import type { FaviconType } from '@fai/friendli-suite/features/common/hooks/use-change-favicon';
import i18n from '@fai/friendli-suite/features/i18n';
import { DedicatedFinetuningStatus } from '@fai/friendli-suite/generated/client/graphql';
import type { Nullish } from '@fai/util/types';

export const finetuningStatusOptions = [
  {
    label: i18n.t('dedicated_endpoints.project.initializing'),
    value: DedicatedFinetuningStatus.Initializing,
  },
  {
    label: i18n.t('dedicated_endpoints.project.training'),
    value: DedicatedFinetuningStatus.Training,
  },
  {
    label: i18n.t('dedicated_endpoints.project.completed'),
    value: DedicatedFinetuningStatus.Completed,
  },
  {
    label: i18n.t('dedicated_endpoints.project.failed'),
    value: DedicatedFinetuningStatus.Failed,
  },
  {
    label: i18n.t('dedicated_endpoints.project.canceled'),
    value: DedicatedFinetuningStatus.Cancelled,
  },
];

export const isFinetuningCancelable = (status: Nullish<DedicatedFinetuningStatus>) => {
  return (
    isNotNil(status) &&
    [DedicatedFinetuningStatus.Initializing, DedicatedFinetuningStatus.Training].includes(status)
  );
};

export const isFinetuningDeleteable = (status: Nullish<DedicatedFinetuningStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedFinetuningStatus.Completed,
      DedicatedFinetuningStatus.Failed,
      DedicatedFinetuningStatus.Cancelled,
    ].includes(status)
  );
};

export const isFinetuningDeployable = (status: Nullish<DedicatedFinetuningStatus>) => {
  return isNotNil(status) && [DedicatedFinetuningStatus.Completed].includes(status);
};

export const isFinetuningDeployTooltipVisible = (status: Nullish<DedicatedFinetuningStatus>) => {
  return (
    isNotNil(status) &&
    [
      DedicatedFinetuningStatus.Initializing,
      DedicatedFinetuningStatus.Training,
      DedicatedFinetuningStatus.Completed,
    ].includes(status)
  );
};

export const shouldFinetuningStatusBePolled = (status: Nullish<DedicatedFinetuningStatus>) => {
  return (
    isNotNil(status) &&
    [DedicatedFinetuningStatus.Initializing, DedicatedFinetuningStatus.Training].includes(status)
  );
};

export const getFaviconTypeFromFinetuningStatus = (
  status: Nullish<DedicatedFinetuningStatus>
): FaviconType => {
  switch (status) {
    case DedicatedFinetuningStatus.Training: {
      return 'success';
    }
    case DedicatedFinetuningStatus.Initializing: {
      return 'pending';
    }
    case DedicatedFinetuningStatus.Failed: {
      return 'failed';
    }

    default: {
      return 'default';
    }
  }
};
